/**
 * Filtres type affichés dans la barre de segmentation.
 * - AGENT  → vue hiérarchique (titulaires + dépliage foyer) en liste active
 * - all    → tous les adhérents, liste à plat
 * - SPOUSE → conjoints + conjointes (backend : IN CONJOINT, CONJOINTE)
 * - ENFANT → enfants, liste à plat
 */
export const MEMBER_TYPE_FILTERS = [
  { id: 'AGENT', label: 'Titulaires' },
  { id: 'all', label: 'Tous' },
  { id: 'SPOUSE', label: 'Conjoint(e)' },
  { id: 'ENFANT', label: 'Enfants' }
];

/** Anciens filtres séparés → regroupement conjoint(e). */
export function normalizeMemberTypeFilter(typeFilter) {
  if (typeFilter === 'CONJOINT' || typeFilter === 'CONJOINTE') return 'SPOUSE';
  return typeFilter;
}

/**
 * Déduit le mode d'affichage et le paramètre `type` API à partir des filtres UI.
 *
 * Règles :
 * - Recherche active → toujours liste à plat ; type = filtre choisi sauf « all » (tous types).
 * - Archives → toujours à plat ; type optionnel selon filtre.
 * - Liste active + Titulaires → hiérarchique, API type AGENT.
 * - Liste active + Tous / Conjoint(e) / Enfants → à plat avec le type adapté.
 */
export function resolveMemberListQuery({
  isArchiveView = false,
  typeFilter = 'AGENT',
  searchTerm = ''
} = {}) {
  const type = normalizeMemberTypeFilter(typeFilter);
  const hasSearch = Boolean(String(searchTerm || '').trim());

  if (hasSearch) {
    // Titulaires / Tous + recherche → tous les types (sinon un conjoint par nom reste invisible).
    const searchType =
      type === 'all' || type === 'AGENT'
        ? undefined
        : type === 'SPOUSE'
          ? 'SPOUSE'
          : type;
    return {
      isHierarchical: false,
      listType: searchType,
      hasSearch: true
    };
  }

  if (isArchiveView) {
    return {
      isHierarchical: false,
      listType: type === 'all' ? undefined : type === 'SPOUSE' ? 'SPOUSE' : type,
      hasSearch: false
    };
  }

  if (type === 'AGENT') {
    return { isHierarchical: true, listType: 'AGENT', hasSearch: false };
  }

  return {
    isHierarchical: false,
    listType: type === 'all' ? undefined : type === 'SPOUSE' ? 'SPOUSE' : type,
    hasSearch: false
  };
}

/** Libellé du compteur résultats (meta + pied de liste). */
export function formatMemberListCountMeta({
  total = 0,
  isHierarchical = false,
  isArchiveView = false,
  hasSearch = false,
  typeFilter = 'AGENT'
} = {}) {
  const type = normalizeMemberTypeFilter(typeFilter);
  if (total === 0) {
    return { countNoun: 'adhérent', empty: true };
  }
  if (hasSearch) {
    return { countNoun: 'résultat', empty: false };
  }
  if (isArchiveView) {
    return { countNoun: 'archivé', empty: false, pluralSuffix: total > 1 ? 's' : '' };
  }
  if (isHierarchical || type === 'AGENT') {
    return { countNoun: 'titulaire', empty: false, hint: 'dépliez pour les ayants-droit' };
  }
  if (type === 'all') {
    return { countNoun: 'adhérent', empty: false };
  }
  return { countNoun: 'adhérent', empty: false };
}

export function formatMemberFullName(member) {
  const name = `${member?.nom || ''} ${member?.prenom || ''}`.trim();
  return name || '—';
}

export function isMemberActive(member) {
  return member?.is_active !== false && member?.is_active !== 0;
}

export function getMemberTypeClass(type) {
  if (type === 'AGENT') return 'ap-mem-type--agent';
  if (type === 'CONJOINT' || type === 'CONJOINTE') return 'ap-mem-type--spouse';
  return 'ap-mem-type--child';
}

export function getMemberTypeLabel(type) {
  if (type === 'AGENT') return 'Agent';
  if (type === 'CONJOINT') return 'Conjoint';
  if (type === 'CONJOINTE') return 'Conjointe';
  if (type === 'ENFANT') return 'Enfant';
  return type || '—';
}

export function formatMemberMetaLine(member, { isChild = false, formatDate } = {}) {
  const parts = [];
  if (member?.matricule) {
    parts.push(member.matricule);
  }
  if (member?.fonction) {
    parts.push(member.fonction);
  } else if (isChild) {
    parts.push('Ayant-droit');
  } else if (member?.sexe) {
    parts.push(member.sexe);
  }
  if (formatDate && member?.date_naissance) {
    const born = formatDate(member.date_naissance);
    if (born && born !== '—') {
      parts.push(`né(e) ${born}`);
    }
  }
  return parts.length ? parts.join(' · ') : null;
}

export function formatMemberCompanyLine(member, getCompanyName) {
  const company = member?.company_name || getCompanyName?.(member?.company_id);
  if (!company || company === '-') {
    return { text: 'Société non renseignée', empty: true };
  }
  return { text: company, empty: false, title: company };
}
